/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {catchError, map, Observable, of} from 'rxjs';

import {AdServerConfig} from '../../config/api-config';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';

export interface TcfCheckResult {
  country: string | null;
  gdpr: string | null;
  tcfPurposes: string[];
  tcfPurposesString: string;
  ipAddress: string | null;
}

@Injectable({providedIn: 'root'})
export class TcfApiService {
  constructor(private readonly client: AdnApiClientService) {
  }

  checkTcf(gdpr: string | number, consentString?: string): Observable<TcfCheckResult | string> {
    const params: Record<string, any> = {};
    if (gdpr === 'GDPR_ON') {
      params.gdpr = 1;
    } else if (gdpr === 'GDPR_OFF') {
      params.gdpr = 0;
    } else if (gdpr !== 'GDPR_AD_UNIT') {
      params.auId = gdpr;
    }
    if (consentString) {
      params.consentString = consentString;
    }

    return this.client.request<string>({
      method: 'GET',
      url: AdServerConfig.obtain().getUri('consent'),
      params,
      responseType: 'text'
    }).pipe(
      map((text) => this.parseResponse(text)),
      catchError((error: any) => of(error?.data?.message || error?.data))
    );
  }

  private parseResponse(text: string): TcfCheckResult | string {
    if (!text.includes('TCF Consent')) {
      return text;
    }
    const countryMatch = text.match(/Country:\s*([A-Z]{2})/);
    const purposeNumbers = text.split('\n').flatMap((line) => {
      if (!line.includes('TCF_PURPOSE_')) {
        return [];
      }
      const number = line.split('TCF_PURPOSE_')[1];
      return number ? [number.trim()] : [];
    });
    const gdprValueMatch = text.match(/GDPR:\s*(true|false)/);
    const ipMatch = text.match(/IP:\s*(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/);
    return {
      country: countryMatch ? countryMatch[1] : null,
      gdpr: gdprValueMatch ? gdprValueMatch[1] : null,
      tcfPurposes: purposeNumbers,
      tcfPurposesString: purposeNumbers.join(', '),
      ipAddress: ipMatch ? ipMatch[1] : null
    };
  }
}
